import { DownOutlined, ExclamationCircleOutlined, ExportOutlined, FileSearchOutlined, FilterOutlined, FireOutlined, PrinterOutlined, StopOutlined, ThunderboltOutlined, UpOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Badge, Button, Checkbox, Drawer, Flex, Input, Modal, Segmented, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type ChannelPlatformName, type OrderStatus, type OrderSummary } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { RefreshButton } from "@/components/RefreshButton";
import { DateRangeFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useIsActiveTab } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { useSetUrlFilters, useUrlFilter } from "@/lib/useUrlFilter";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { PLATFORMS, useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";
import { CaseProgressTag } from "../aftersales/common";
import { HoldTag, ORDER_HOLDS, ORDER_STATUSES, OrderStateTag, usePlatformStatus, type OrderHold } from "./OrderStatusTag";
import { ArrangeModal, useLabelPrinter, type ShipTarget } from "./shipping";
import { ShipBy } from "./ShipBy";
import { SavedViews } from "@/components/list/SavedViews";
import { OrderDetailView } from "./OrderDetail";
import { CourierKindTag } from "./CourierKind";
import { WaveOrderTag } from "../warehouse/common";

// held: orders a hold stops, shown apart from Baru and Diproses.
type StatusFilter = OrderStatus | "all" | "held";
type LabelFilter = "" | "printed" | "unprinted";
// Orders still to be worked on: mapping a line or printing a label matters only to these.
const OPEN: OrderStatus[] = ["awaiting_payment", "new", "processing", "packed"];
// Tahap gudang of orders being processed: not in a wave yet, picking, packing.
const STAGES = ["queue", "picking", "packing"] as const;
type Stage = (typeof STAGES)[number];

/** Order pusat (App Flow §6): every store's orders in one list, filtered by status tab. */
export function Orders() {
  const { t, formatDateTime, formatMoney, formatNumber } = useI18n();
  const platformStatus = usePlatformStatus();
  const { me, can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const activeTab = useIsActiveTab();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");

  // Filters live in the URL so they stay with this tab (lib/useUrlFilter.ts).
  // Opens on Diproses, the work to do now.
  const [status, setStatus] = useUrlFilter<StatusFilter>("status", "processing", ["all", "held", ...ORDER_STATUSES]);
  const [platform, setPlatform] = useUrlFilter<ChannelPlatformName | "">("platform", "", ["", ...PLATFORMS]);
  const [connectionId, setConnectionId] = useUrlFilter<string>("store", "");
  const [pickedWarehouse, setWarehouseId] = useUrlFilter<string>("warehouse", "");
  const range = useDateRange();
  const page = useListPage();
  const [courier, setCourier] = useUrlFilter<string>("courier", "");
  const [priority, setPriority] = useUrlFilter<"" | Priority>("priority", "", ["", ...PRIORITIES]);
  // Which date the range filters: when ordered, when it came into Zerpid, or its ship-by deadline.
  const [dateBy, setDateBy] = useUrlFilter<DateBy>("dateBy", "ordered", DATE_BYS);
  const couriers = useQuery({ queryKey: ["couriers"], queryFn: () => unwrap(api.GET("/v1/couriers")), staleTime: 300_000 });
  const [cancelOnly, setCancelOnly] = useUrlFilter<"" | "1">("cancel", "", ["", "1"]);
  const [labelFilter, setLabelFilter] = useUrlFilter<LabelFilter>("label", "", ["", "printed", "unprinted"]);
  const [handled, setHandled] = useUrlFilter<"" | "outside">("handled", "", ["", "outside"]);
  const [stage, setStage] = useUrlFilter<"" | Stage>("stage", "", ["", ...STAGES]);
  const [holdFilter, setHoldFilter] = useUrlFilter<"" | "any" | OrderHold>("hold", "", ["", "any", ...ORDER_HOLDS]);
  const [caseFilter, setCaseFilter] = useUrlFilter<"" | "active" | "respond" | "any">("aftersales", "", ["", "active", "respond", "any"]);
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const [due, setDue] = useUrlFilter<"" | Due>("due", "", ["", ...DUES]);
  // Many order or tracking numbers pasted at once (comma separated in the URL).
  const [numbers, setNumbers] = useUrlFilter<string>("numbers", "");
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const numberCount = numbers ? numbers.split(",").filter(Boolean).length : 0;
  const q = useDeferredValue(search.trim());
  // The less used filters fold away; open when one of them is set.
  const advanced = [platform, connectionId, pickedWarehouse, holdFilter, caseFilter, labelFilter, handled, priority, courier, due, range.query.from, range.query.to].filter(Boolean).length;
  const [showFilters, setShowFilters] = useState(advanced > 0);
  const setFilters = useSetUrlFilters();
  const resetFilters = () =>
    setFilters({ platform: "", store: "", warehouse: "", hold: "", aftersales: "", label: "", handled: "", priority: "", courier: "", due: "", from: "", to: "", dateBy: "" });
  const [selected, setSelected] = useState<string[]>([]);
  const [arranging, setArranging] = useState<ShipTarget[] | null>(null);
  // The order shown in the side panel (a quick look; its own tab for longer work).
  const [peek, setPeek] = useState<string | null>(null);
  const printer = useLabelPrinter();

  const connections = useConnections();
  const warehouses = useWarehouses();
  // Members limited to some warehouses see one at a time, the active one first.
  const activeWarehouse = useActiveWarehouse().current?.id ?? "";
  const warehouseId = pickedWarehouse || (warehouses.restricted ? activeWarehouse : "");

  const filters = {
    ...(status === "held" ? { held: true } : status !== "all" ? { status } : {}),
    ...(status === "processing" && stage ? { stage } : {}),
    ...(platform ? { platform } : {}),
    ...(connectionId ? { connectionId } : {}),
    ...(warehouseId ? { warehouseId } : {}),
    ...(range.query.from ? { [{ ordered: "from", created: "createdFrom", shipBy: "shipByFrom", shipped: "shippedFrom" }[dateBy]]: range.query.from } : {}),
    ...(range.query.to ? { [{ ordered: "to", created: "createdTo", shipBy: "shipByTo", shipped: "shippedTo" }[dateBy]]: range.query.to } : {}),
    ...(courier ? { courier } : {}),
    ...(due ? { due } : {}),
    ...(numbers ? { numbers } : {}),
    ...(priority ? { priority } : {}),
    ...(cancelOnly ? { cancelRequested: true } : {}),
    ...(labelFilter ? { labelPrinted: labelFilter === "printed" } : {}),
    ...(handled === "outside" ? { outside: true } : {}),
    ...(holdFilter === "any" ? { held: true } : holdFilter ? { hold: holdFilter } : {}),
    ...(caseFilter ? { aftersales: caseFilter } : {}),
    ...(q ? { q } : {}),
  };
  const list = useQuery({
    queryKey: ["orders", "list", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/orders", { params: { query: { ...filters, ...page.query } } })),
    // New orders arrive by webhook; refresh while the tab is in view.
    refetchInterval: activeTab ? 30_000 : false,
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const first = list.data;
  // Counted only for statuses that still need work; history is not counted.
  const counts = first?.counts ?? {};
  const processable = (o: OrderSummary) => o.status === "new" && !o.cancelRequested;
  const arrangeable = (o: OrderSummary) => (o.status === "new" || o.status === "processing") && !o.shippingArranged && !o.cancelRequested;
  const printable = (o: OrderSummary) => o.shippingArranged && o.status !== "cancelled" && o.status !== "returned";
  // Selection spans every action; each button acts on the selected orders it applies to.
  const picked = rows.filter((o) => selected.includes(o.id));
  const chosen = picked.filter(processable).map((o) => o.id);
  const toArrange = picked.filter(arrangeable);
  const toPrint = picked.filter(printable);
  const asTarget = (o: OrderSummary): ShipTarget => ({ id: o.id, externalId: o.externalId, platform: o.platform });

  const processBulk = useMutation({
    mutationFn: (ids: string[]) => unwrap(api.POST("/v1/orders/process", { body: { ids } })),
    onSuccess: ({ results }) => {
      const ok = results.filter((r) => r.ok).length;
      const failed = results.length - ok;
      if (failed === 0) void message.success(t("orders.processed", { ok }));
      else void message.warning(t("orders.processedPartial", { ok, failed }));
      setSelected([]);
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }} wrap gap={12}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("orders.title")}
        </Typography.Title>
        {manage && (
          <Space wrap>
            <Button onClick={() => navigate("/app/orders/new")}>{t("manual.add")}</Button>
            <Button onClick={() => navigate("/app/orders/hold-rules")}>{t("orders.holdRules")}</Button>
            {picked.length > 0 && (
              <>
                <Typography.Text type="secondary">{t("orders.selectedCount", { n: picked.length })}</Typography.Text>
                <Button type="link" onClick={() => setSelected([])}>
                  {t("orders.clearSelection")}
                </Button>
              </>
            )}
            <Button disabled={toPrint.length === 0} icon={<PrinterOutlined />} onClick={() => printer.print(toPrint.map(asTarget))}>
              {t("orders.printSelected", { n: toPrint.length })}
            </Button>
            <Button disabled={toArrange.length === 0} onClick={() => setArranging(toArrange.map(asTarget))}>
              {t("orders.arrangeSelected", { n: toArrange.length })}
            </Button>
            <Button type="primary" disabled={chosen.length === 0} loading={processBulk.isPending} onClick={() => processBulk.mutate(chosen)}>
              {t("orders.processSelected", { n: chosen.length })}
            </Button>
          </Space>
        )}
      </Flex>

      <div style={{ overflowX: "auto", marginBottom: 12 }}>
        <Segmented<StatusFilter>
          value={status}
          onChange={(v) => {
            setStatus(v);
            setSelected([]);
          }}
          options={[
            { value: "all", label: t("orders.all") },
            {
              value: "held",
              label: (
                <span>
                  {t("orders.heldTab")}
                  {first?.held && <span className="z-num"> {formatNumber(Object.values(first.held).reduce((a, b) => a + b, 0))}</span>}
                </span>
              ),
            },
            ...ORDER_STATUSES.map((s) => ({
              value: s,
              label: (
                <span>
                  {t(`status.${s}`)}
                  {counts[s] !== undefined && <span className="z-num"> {formatNumber(counts[s])}</span>}
                </span>
              ),
            })),
          ]}
        />
      </div>
      {status === "processing" && (
        <div style={{ overflowX: "auto", marginBottom: 12 }}>
          <Segmented<"" | Stage>
            size="small"
            value={stage}
            onChange={(v) => {
              setStage(v);
              setSelected([]);
            }}
            options={[
              { value: "" as const, label: t("orders.stage.all") },
              ...STAGES.map((s) => ({
                value: s,
                label: (
                  <span>
                    {t(`orders.stage.${s}`)}
                    {first?.stages && <span className="z-num"> {formatNumber(first.stages[s])}</span>}
                  </span>
                ),
              })),
            ]}
          />
        </div>
      )}

      <Flex gap={12} wrap align="center" style={{ marginBottom: 12 }}>
        <Space.Compact>
          <Input.Search allowClear placeholder={t("orders.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 300 }} />
          <Tooltip title={t("orders.bulk.button")}>
            <Button icon={<FileSearchOutlined />} onClick={() => { setPasted(numbers.split(",").join("\n")); setPasting(true); }} />
          </Tooltip>
        </Space.Compact>
        {numberCount > 0 && (
          <Tag closable color="blue" onClose={() => setNumbers("")}>
            {t("orders.bulk.active", { n: numberCount, found: first?.total ?? 0 })}
          </Tag>
        )}
        <Badge count={advanced} size="small">
          <Button icon={<FilterOutlined />} type={showFilters ? "primary" : "default"} ghost={showFilters} onClick={() => setShowFilters((v) => !v)}>
            {t("orders.filters")}
          </Button>
        </Badge>
        {advanced > 0 && (
          <Button type="link" onClick={resetFilters} style={{ paddingInline: 4 }}>
            {t("orders.filtersReset")}
          </Button>
        )}
        {(first?.cancelRequests ?? 0) > 0 || cancelOnly ? (
          <Checkbox checked={cancelOnly === "1"} onChange={(e) => setCancelOnly(e.target.checked ? "1" : "")}>
            {t("orders.cancelRequestsOnly", { n: first?.cancelRequests ?? 0 })}
          </Checkbox>
        ) : null}
        <div style={{ flex: 1 }} />
        <RefreshButton queryKeys={[["orders"]]} />
        <ExportButton
          path="/v1/orders/export"
          query={filters}
          filename={t("orders.title")}
          columns={[
            { key: "externalId", label: t("orders.number") },
            { key: "trackingNo", label: t("orders.tracking") },
            { key: "platform", label: t("channels.colMarketplace"), values: Object.fromEntries(PLATFORMS.map((p) => [p, t(`platform.${p}`)])) },
            { key: "shopName", label: t("orders.store") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(ORDER_STATUSES.map((v) => [v, t(`status.${v}`)])) },
            { key: "platformStatus", label: t("orders.platformStatus") },
            { key: "hold", label: t("orders.holdLabel"), values: Object.fromEntries(ORDER_HOLDS.map((h) => [h, t(`orders.hold.${h}`)])) },
            { key: "holdNote", label: t("orders.holdNoteLabel") },
            { key: "cancelRequested", label: t("orders.cancelRequested"), values: { true: t("common.yes"), false: t("common.no") } },
            { key: "buyerName", label: t("orders.buyer") },
            { key: "recipientName", label: t("orders.recipient") },
            { key: "lineCount", label: t("orders.lineCount"), kind: "number" },
            { key: "unitCount", label: t("orders.unitCount"), kind: "number" },
            { key: "totalAmount", label: t("orders.total"), kind: "number" },
            { key: "currency", label: t("orders.currency") },
            { key: "warehouseId", label: t("orders.warehouse"), values: Object.fromEntries(warehouses.active.map((w) => [w.id, w.name])) },
            { key: "carrier", label: t("orders.carrier") },
            { key: "shippingArranged", label: t("orders.arranged"), values: { true: t("common.yes"), false: t("common.no") } },
            { key: "labelPrints", label: t("orders.labelPrints"), kind: "number" },
            { key: "shipBy", label: t("orders.shipBy"), kind: "datetime" },
            { key: "orderedAt", label: t("orders.orderedAt"), kind: "datetime" },
          ]}
        />
      </Flex>

      {showFilters && (
        <div style={{ background: "var(--ant-color-fill-quaternary)", borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <Flex gap={6} wrap align="center" style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t("orders.due.title")}
            </Typography.Text>
            {(["", ...DUES] as const).map((d) => (
              <Tag.CheckableTag
                key={d || "all"}
                checked={due === d}
                onChange={() => setDue(d)}
                style={{ border: "1px solid var(--ant-color-border)", padding: "2px 8px" }}
              >
                {t(`orders.due.${d || "all"}`)}
              </Tag.CheckableTag>
            ))}
          </Flex>
          <Flex gap={8} wrap style={{ marginBottom: 12 }}>
            <Select<ChannelPlatformName | "">
              value={platform}
              onChange={setPlatform}
              style={{ width: 170 }}
              options={[{ value: "", label: t("channels.colMarketplace") }, ...[...PLATFORMS, "manual" as const].map((p) => ({ value: p, label: t(`platform.${p}`) }))]}
            />
            {can("channels.view") && (
              <Select<string>
                value={connectionId}
                onChange={setConnectionId}
                style={{ width: 200 }}
                showSearch={{ optionFilterProp: "label" }}
                options={[
                  { value: "", label: t("orders.allStores") },
                  ...(connections.data?.items ?? []).filter((c) => !platform || c.platform === platform).map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            )}
            {(warehouses.accessible.length > 1 || (!warehouses.restricted && warehouses.active.length > 1)) && (
              <Select<string>
                value={warehouseId}
                onChange={setWarehouseId}
                style={{ width: 180 }}
                options={[
                  ...(warehouses.restricted ? [] : [{ value: "", label: t("inventory.allWarehouses") }]),
                  ...warehouses.accessible.map((w) => ({ value: w.id, label: w.name })),
                ]}
              />
            )}
            <Select<"" | "any" | OrderHold>
              value={holdFilter}
              onChange={setHoldFilter}
              style={{ width: 210 }}
              options={[
                { value: "", label: t("orders.holdFilter.all") },
                { value: "any", label: t("orders.holdFilter.any", { n: Object.values(first?.held ?? {}).reduce((a, b) => a + b, 0) }) },
                ...ORDER_HOLDS.map((h) => ({ value: h, label: `${t(`orders.hold.${h}`)} (${first?.held?.[h] ?? 0})` })),
              ]}
            />
            <Select<"" | "active" | "respond" | "any">
              value={caseFilter}
              onChange={setCaseFilter}
              style={{ width: 210 }}
              options={(["", "respond", "active", "any"] as const).map((v) => ({ value: v, label: t(`orders.caseFilter.${v || "all"}`) }))}
            />
            <Select<LabelFilter>
              value={labelFilter}
              onChange={setLabelFilter}
              style={{ width: 190 }}
              options={[
                { value: "", label: t("orders.labelFilter.all") },
                { value: "unprinted", label: t("orders.labelFilter.unprinted") },
                { value: "printed", label: t("orders.labelFilter.printed") },
              ]}
            />
            <Select<"" | "outside">
              value={handled}
              onChange={setHandled}
              style={{ width: 190 }}
              options={[
                { value: "", label: t("orders.handled.all") },
                { value: "outside", label: t("orders.outside") },
              ]}
            />
            <Select<"" | Priority>
              value={priority}
              onChange={setPriority}
              style={{ width: 190 }}
              options={[{ value: "" as const, label: t("orders.priority.all") }, ...PRIORITIES.map((p) => ({ value: p, label: t(`orders.priority.${p}`) }))]}
            />
            <Select<string>
              value={courier}
              onChange={setCourier}
              style={{ width: 190 }}
              showSearch={{ optionFilterProp: "label" }}
              options={[{ value: "", label: t("orders.courierFilter.all") }, ...(couriers.data?.couriers ?? []).map((c) => ({ value: c.name, label: c.name }))]}
            />
            <Space.Compact>
              <Select<DateBy> value={dateBy} onChange={setDateBy} style={{ width: 170 }} options={DATE_BYS.map((v) => ({ value: v, label: t(`orders.dateBy.${v}`) }))} />
              <DateRangeFilter range={range} />
            </Space.Compact>
          </Flex>
          <SavedViews page="orders" />
        </div>
      )}

      <Table<OrderSummary>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1280 }}
        locale={{
          emptyText: (
            <Space orientation="vertical" size={4} style={{ padding: 24 }}>
              <Typography.Text strong>{t("orders.empty")}</Typography.Text>
              <Typography.Text type="secondary">{t("orders.emptyBody")}</Typography.Text>
            </Space>
          ),
        }}
        rowSelection={
          manage
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
              }
            : undefined
        }
        rowClassName={(o) => (o.id === peek ? "z-row-peek" : "")}
        onRow={(o) => ({
          onClick: (e) => {
            // Checkboxes, links and copy buttons do their own thing.
            if ((e.target as HTMLElement).closest("a, button, input, label, .ant-table-selection-column, .ant-typography-copy")) return;
            setPeek(o.id);
          },
          onDoubleClick: () => navigate(`/app/orders/${o.id}`),
        })}
        columns={[
          {
            title: t("orders.number"),
            dataIndex: "externalId",
            width: 210,
            render: (v: string, o) => (
              <Space orientation="vertical" size={2}>
                <Space size={4}>
                  <Typography.Link
                    className="z-num"
                    onClick={(e) => (e.ctrlKey || e.metaKey ? navigate(`/app/orders/${o.id}`) : setPeek(o.id))}
                  >
                    {v}
                  </Typography.Link>
                  {/* Copy is its own icon: the number itself opens the order. */}
                  <Typography.Text copyable={{ text: v, tooltips: [t("orders.copyNumber"), t("orders.copied")] }} />
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: o.buyerName || o.recipientName }}>
                  {o.buyerName || o.recipientName || "—"}
                </Typography.Text>
                <Space size={4} wrap>
                  <PriorityTag o={o} />
                  {o.cod && <Tag color="gold">{t("orders.cod")}</Tag>}
                  {(o.splitCombine || o.combineGroup) && (
                    <Tooltip title={t(o.splitCombine === "SPLIT" ? "orders.splitHelp" : "orders.combinedHelp")}>
                      <Tag color="purple">{t(o.splitCombine === "SPLIT" ? "orders.splitTag" : "orders.combinedTag")}</Tag>
                    </Tooltip>
                  )}
                  {o.cancelRequested && (
                    <Tag color="error" icon={<StopOutlined />}>
                      {t("orders.cancelRequested")}
                    </Tag>
                  )}
                  {o.hold
                    ? // A hold shows in the status column unless the status itself goes first.
                      !(o.status === "new" || o.status === "processing") && <HoldTag hold={o.hold} note={o.holdNote} />
                    : o.hasUnmapped &&
                      OPEN.includes(o.status) && (
                        <Tooltip title={t("orders.unmappedHelp")}>
                          <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                            {t("orders.unmapped")}
                          </Tag>
                        </Tooltip>
                      )}
                </Space>
              </Space>
            ),
          },
          {
            title: t("orders.store"),
            dataIndex: "shopName",
            render: (v: string, o) => (
              <Space orientation="vertical" size={0}>
                <PlatformLabel platform={o.platform} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {v}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("orders.tracking"),
            dataIndex: "trackingNo",
            width: 190,
            render: (v: string, o) => (
              <Space orientation="vertical" size={2}>
                {v ? (
                  <Typography.Text className="z-num" copyable={{ text: v }}>
                    {v}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )}
                {o.shippingArranged &&
                  (o.labelPrints > 0 ? (
                    <Tag color="success">{t("orders.labelPrinted", { n: o.labelPrints })}</Tag>
                  ) : (
                    OPEN.includes(o.status) && <Tag color="processing">{t("orders.labelNotPrinted")}</Tag>
                  ))}
              </Space>
            ),
          },
          {
            title: t("orders.wave"),
            width: 150,
            render: (_, o) =>
              o.wave ? (
                <Space orientation="vertical" size={2}>
                  {can("warehouse.operate") ? (
                    <Typography.Link className="z-num" onClick={() => navigate(`/app/warehouse/waves/${o.wave!.id}`)}>
                      {o.wave.number}
                    </Typography.Link>
                  ) : (
                    <span className="z-num">{o.wave.number}</span>
                  )}
                  <WaveOrderTag status={o.wave.status} />
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: t("orders.items"),
            width: 150,
            render: (_, o) => <span className="z-num">{t("orders.itemsValue", { units: o.unitCount, lines: o.lineCount })}</span>,
          },
          {
            title: t("orders.total"),
            dataIndex: "totalAmount",
            width: 140,
            align: "right",
            render: (v: number, o) => <span className="z-num">{formatMoney(v, o.currency)}</span>,
          },
          {
            title: t("common.status"),
            dataIndex: "status",
            width: 210,
            render: (_: OrderStatus, o) => (
              <Space orientation="vertical" size={2}>
                <OrderStateTag o={o} />
                {o.outside && (
                  <Tooltip title={t("orders.outsideHelp")}>
                    <Tag>{t("orders.outside")}</Tag>
                  </Tooltip>
                )}
                {o.platformStatus && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {platformStatus(o.platformStatus)}
                  </Typography.Text>
                )}
                {o.aftersales && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <CaseProgressTag c={o.aftersales} onClick={() => navigate(`/app/aftersales/${o.aftersales!.id}`)} />
                  </span>
                )}
              </Space>
            ),
          },
          {
            title: t("orders.shipBy"),
            dataIndex: "shipBy",
            width: 150,
            render: (v: string | null, o) => (
              <Space orientation="vertical" size={0}>
                <ShipBy at={v} done={o.status !== "new" && o.status !== "processing"} />
                {(o.courier || o.carrier) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} title={o.carrier}>
                    {o.courier || o.carrier}
                  </Typography.Text>
                )}
                <CourierKindTag kind={o.courierKind} />
              </Space>
            ),
          },
          {
            title: t("orders.orderedAt"),
            dataIndex: "orderedAt",
            width: 170,
            render: (v: string | null) => (v ? formatDateTime(v, tz) : "—"),
          },
        ]}
      />
      <ArrangeModal targets={arranging} onClose={() => setArranging(null)} />
      {printer.ui}
      <Modal
        open={pasting}
        title={t("orders.bulk.title")}
        okText={t("orders.bulk.ok")}
        cancelText={t("common.cancel")}
        onOk={() => {
          const list = [...new Set(pasted.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean))].slice(0, 500);
          setNumbers(list.join(","));
          setPasting(false);
        }}
        onCancel={() => setPasting(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">{t("orders.bulk.help")}</Typography.Paragraph>
        <Input.TextArea autoFocus rows={10} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={"250918ABC123\nJP1234567890\n..."} />
      </Modal>
      <OrderPeek
        id={peek}
        ids={rows.map((o) => o.id)}
        onMove={setPeek}
        onOpenTab={(id) => {
          setPeek(null);
          navigate(`/app/orders/${id}`);
        }}
        onClose={() => setPeek(null)}
      />
    </>
  );
}

const PRIORITIES = ["urgent", "instant", "due_soon", "late"] as const;
type Priority = (typeof PRIORITIES)[number];
const DATE_BYS = ["ordered", "created", "shipBy", "shipped"] as const;
type DateBy = (typeof DATE_BYS)[number];

function msLeft(iso: string): number {
  return new Date(iso).getTime() - Date.now();
}

/** Why an open order goes first: late, due within a few hours, or an instant courier. */
function PriorityTag({ o }: { o: OrderSummary }) {
  const { t } = useI18n();
  if (!["new", "processing", "packed"].includes(o.status)) return null;
  const left = o.shipBy ? msLeft(o.shipBy) : Infinity;
  if (left < 0) return <Tag color="error" icon={<FireOutlined />}>{t("orders.priority.lateTag")}</Tag>;
  if (left < 6 * 3600_000) return <Tag color="volcano" icon={<FireOutlined />}>{t("orders.priority.soonTag", { n: Math.max(1, Math.round(left / 3600_000)) })}</Tag>;
  if (o.courierKind === "instant" || o.courierKind === "sameday") return <Tag color="orange" icon={<ThunderboltOutlined />}>{t(`courier.kind.${o.courierKind}`)}</Tag>;
  return null;
}

/**
 * The order beside the list: the list stays as it is (filters, scroll, the
 * row it came from); up and down go to the previous and next order.
 */
function OrderPeek({ id, ids, onMove, onOpenTab, onClose }: {
  id: string | null;
  ids: string[];
  onMove: (id: string) => void;
  onOpenTab: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const at = id ? ids.indexOf(id) : -1;
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest("input, textarea, [contenteditable=true], .ant-select")) return;
      if (e.key === "ArrowDown" && at >= 0 && at < ids.length - 1) {
        e.preventDefault();
        onMove(ids[at + 1]!);
      } else if (e.key === "ArrowUp" && at > 0) {
        e.preventDefault();
        onMove(ids[at - 1]!);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id, at, ids, onMove]);
  // No mask, so the list stays usable: a click on another order switches the
  // panel, a click anywhere else outside it closes it. Popups the panel opens
  // (selects, modals, pickers) live in portals and do not count as outside.
  useEffect(() => {
    if (!id) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.isConnected || el.closest(".z-order-peek, .ant-table-row, .ant-modal-root, .ant-dropdown, .ant-select-dropdown, .ant-picker-dropdown, .ant-popover, .ant-message, .ant-notification, .ant-image-preview-root")) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [id, onClose]);
  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      size={1040}
      mask={false}
      rootClassName="z-order-peek"
      destroyOnHidden
      title={
        <Flex justify="space-between" align="center" gap={8}>
          <Space size={4}>
            <Button size="small" icon={<UpOutlined />} disabled={at <= 0} onClick={() => onMove(ids[at - 1]!)} title={t("orders.peek.prev")} />
            <Button size="small" icon={<DownOutlined />} disabled={at < 0 || at >= ids.length - 1} onClick={() => onMove(ids[at + 1]!)} title={t("orders.peek.next")} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("orders.peek.keys")}
            </Typography.Text>
          </Space>
          {id && (
            <Button size="small" icon={<ExportOutlined />} onClick={() => onOpenTab(id)}>
              {t("orders.peek.openTab")}
            </Button>
          )}
        </Flex>
      }
    >
      {id && <OrderDetailView key={id} orderID={id} inPanel />}
    </Drawer>
  );
}

const DUES = ["expired", "0_3", "3_6", "6_12", "12_48", "48_plus", "none"] as const;
type Due = (typeof DUES)[number];
